// Download helpers: turn a captured take into a file the browser
// saves to disk. For .wav the pipeline is:
//   captured blob (webm/mp4) → decodeAudioData → AudioBuffer → encodeWav
// For the raw blob we just download whatever MediaRecorder produced.
//
// decodeAudioData needs an AudioContext. We piggyback on Strudel's,
// since creating a second context costs memory and doesn't work on
// Safari (webkit caps context count). If none is available (shouldn't
// happen in practice — there's no recording without an engine), we
// fall back to a fresh AudioContext.

import { encodeWav } from '../samples/wav';

export type DecodeFn = (bytes: ArrayBuffer) => Promise<AudioBuffer>;

export async function convertToWav(
  blob: Blob,
  decoder: DecodeFn,
): Promise<Blob> {
  const bytes = await blob.arrayBuffer();
  const audioBuffer = await decoder(bytes);
  return encodeWav(audioBuffer);
}

/** Default decoder built from a live AudioContext. Safe to pass when
 *  the context is running; callers should pass an already-captured
 *  context rather than creating a new one each download. */
export function makeDecoder(ctx: AudioContext): DecodeFn {
  return (bytes: ArrayBuffer) => {
    // Promise-form of decodeAudioData. Strudel's AudioContext is the
    // modern one, so the returned Promise is always defined.
    return ctx.decodeAudioData(bytes);
  };
}

export function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Release the object URL on the next tick — revoking too fast in
  // Safari can truncate the download.
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1_000);
}

/** Build a readable filename from a take name + extension. Strips
 *  characters that filesystems or URLs would choke on. */
export function filenameFor(takeName: string, extension: string): string {
  const base =
    takeName
      .trim()
      .replace(/[^A-Za-z0-9 \-_]/g, '')
      .replace(/\s+/g, '_')
      .slice(0, 60) || 'take';
  const ext = extension.replace(/^\./, '');
  return `${base}.${ext}`;
}
